// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Daily settlement ledger, derived from HITL plan approvals.
 *
 * There is no dedicated settlement API: once the realized-KPI listener has
 * settled a day it writes the outcome back onto that day's plan approval as
 * `realizedSavingsUsd` (the day counted) or `missedSavingsUsd` (the day was
 * left on the table). An approval carrying neither is simply not settled yet
 * and stays out of the ledger.
 */

import type { HitlApproval, SettlementLedgerRow } from '../types';
import { getNumberAttr, planExecutionDay } from './planAttributes';
import { browserTimeZone, formatCalendarDate, nextDayKey, zonedDayKey } from './siteTime';

/**
 * Settlement day for a row: the plan's execution day. A plan with none has no
 * day to settle against and is dropped by the caller.
 */
function settlementDate(approval: HitlApproval): string | undefined {
  return planExecutionDay(approval.attributes);
}

/**
 * Settled approvals as ledger rows, newest day first. Rows without a realized
 * *or* missed figure, or without a settlement date, are excluded.
 */
export function buildSettlementLedger(approvals: HitlApproval[]): SettlementLedgerRow[] {
  const rows: SettlementLedgerRow[] = [];
  for (const approval of approvals) {
    const realized = getNumberAttr(approval.attributes, 'realizedSavingsUsd');
    const missed = getNumberAttr(approval.attributes, 'missedSavingsUsd');
    if (realized === undefined && missed === undefined) continue;
    const date = settlementDate(approval);
    if (!date) continue;
    rows.push({
      id: approval.approvalId,
      date,
      approved: approval.state === 'APPROVED',
      // Realized wins when a day somehow carries both: money actually booked
      // is the stronger signal than an estimate of what was left behind.
      status: realized !== undefined ? 'counted' : 'unrealized',
      expectedUsd: getNumberAttr(approval.attributes, 'totalCostSavings'),
      realizedOrMissedUsd: realized !== undefined ? realized : missed,
    });
  }
  return rows.sort((a, b) => b.date.localeCompare(a.date));
}

/** "2026-07-28" → "Jul 28, 2026". Formatted in UTC so the day can't shift. */
export function formatLedgerDate(date: string): string {
  return formatCalendarDate(date, 'dateMedium', date);
}

/** Whole-dollar USD, e.g. `$1,240`. Undefined renders as an em dash. */
export function formatUsd(value: number | undefined): string {
  if (value === undefined) return '—';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * A settled figure as the ledger states it: `$1,180 est.`. These are reconciled
 * monthly, so the column never presents one as final. An unsettled day keeps the
 * bare em dash — `— est.` would qualify a number that isn't there.
 */
export function formatUsdEstimate(value: number | undefined): string {
  if (value === undefined) return formatUsd(value);
  return `${formatUsd(value)} est.`;
}

/** Calendar day as YYYY-MM-DD on the site's clock (never UTC — see formatLedgerDate). */
function toDayString(date: Date, timeZone?: string): string {
  return zonedDayKey(date, timeZone ?? browserTimeZone());
}

/**
 * Default ledger window: the 1st of the current month through tomorrow.
 *
 * Month-to-date is what the KPI tiles above the table report, so the ledger
 * opens on the same period. The upper bound is tomorrow rather than today
 * because a day-ahead plan settles against its execution date, which is
 * already on the books before it runs.
 *
 * Computed on the site's calendar, so a viewer west of the site does not open on the wrong day.
 */
export function defaultLedgerRange(
  now: Date = new Date(),
  timeZone?: string
): { from: string; to: string } {
  const today = toDayString(now, timeZone);
  const tomorrow = nextDayKey(today);
  return { from: `${today.slice(0, 7)}-01`, to: tomorrow };
}

export interface LedgerFilters {
  /** Inclusive YYYY-MM-DD bounds; an omitted bound is unbounded on that side. */
  from?: string;
  to?: string;
}

/** Apply the toolbar's date range to the ledger rows. */
export function filterLedger(
  rows: SettlementLedgerRow[],
  { from, to }: LedgerFilters
): SettlementLedgerRow[] {
  return rows.filter(row => {
    if (from && row.date < from) return false;
    if (to && row.date > to) return false;
    return true;
  });
}

/**
 * Quote a CSV cell only when it needs it. A bare CR counts: values copied from
 * a CRLF source can carry one, and unquoted it breaks the row structure just as
 * an LF would.
 */
function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The ledger as CSV — raw numbers, not the display-rounded currency strings. */
export function ledgerToCsv(rows: SettlementLedgerRow[]): string {
  const lines = [
    ['Date', 'Plan approval', 'Status', 'Expected (USD)', 'Realized (USD)'],
    ...rows.map(row => [
      row.date,
      row.approved ? 'Approved' : 'Not approved',
      row.status === 'counted' ? 'Counted' : 'Unrealized',
      row.expectedUsd === undefined ? '' : String(row.expectedUsd),
      row.realizedOrMissedUsd === undefined ? '' : String(row.realizedOrMissedUsd),
    ]),
  ];
  return lines.map(cols => cols.map(csvCell).join(',')).join('\r\n');
}
